import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  noindex?: boolean;
  structuredData?: object;
}

const SEOComponent = ({ 
  title, 
  description, 
  canonical, 
  keywords,
  ogImage,
  ogType = 'website',
  publishedTime,
  modifiedTime,
  author,
  noindex = false,
  structuredData
}: SEOProps) => {
  const location = useLocation();
  const baseUrl = 'https://www.ai4me.com.br';
  const fullUrl = `${baseUrl}${location.pathname}`;
  const defaultImage = `${baseUrl}/lovable-uploads/c4f8b3f8-f36b-45d0-95d4-097a69303b76.png`;
  
  useEffect(() => {
    // Helper to update or create meta tag
    const updateMetaTag = (selector: string, attribute: string, content: string) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        if (selector.includes('property')) {
          tag.setAttribute('property', selector.match(/\[property="([^"]+)"\]/)?.[1] || '');
        } else {
          tag.setAttribute('name', selector.match(/\[name="([^"]+)"\]/)?.[1] || '');
        }
        document.head.appendChild(tag);
      }
      tag.setAttribute(attribute, content);
    };

    // Update title
    if (title) {
      document.title = title;
      updateMetaTag('meta[property="og:title"]', 'content', title);
      updateMetaTag('meta[name="twitter:title"]', 'content', title);
    }
    
    // Update meta description
    if (description) {
      updateMetaTag('meta[name="description"]', 'content', description);
      updateMetaTag('meta[property="og:description"]', 'content', description);
      updateMetaTag('meta[name="twitter:description"]', 'content', description);
    }
    
    // Update canonical URL
    const canonicalUrl = canonical ? `${baseUrl}${canonical}` : fullUrl;
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute('href', canonicalUrl);
    
    // Update keywords
    if (keywords) {
      updateMetaTag('meta[name="keywords"]', 'content', keywords);
    }
    
    // Update robots meta tag
    const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow';
    updateMetaTag('meta[name="robots"]', 'content', robotsContent);
    
    // Update Open Graph tags
    updateMetaTag('meta[property="og:url"]', 'content', fullUrl);
    updateMetaTag('meta[property="og:type"]', 'content', ogType);
    
    const imageUrl = ogImage ? `${baseUrl}${ogImage}` : defaultImage;
    updateMetaTag('meta[property="og:image"]', 'content', imageUrl);
    updateMetaTag('meta[name="twitter:image"]', 'content', imageUrl);
    
    // Update article meta tags if provided
    if (publishedTime) {
      updateMetaTag('meta[property="article:published_time"]', 'content', publishedTime);
    }
    if (modifiedTime) {
      updateMetaTag('meta[property="article:modified_time"]', 'content', modifiedTime);
    }
    if (author) {
      updateMetaTag('meta[name="author"]', 'content', author);
      updateMetaTag('meta[property="article:author"]', 'content', author);
    }
    
    // Update structured data if provided
    if (structuredData) {
      let scriptTag = document.querySelector('script[data-seo-structured-data="true"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.setAttribute('data-seo-structured-data', 'true');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(structuredData);
    }
    
  }, [title, description, canonical, keywords, ogImage, ogType, publishedTime, modifiedTime, author, noindex, structuredData, location.pathname, fullUrl, baseUrl]);

  return null;
};

export default SEOComponent;